import React, { useEffect, useState } from 'react';
import { Loader2, Check, X, CheckCircle2, XCircle, RefreshCcw, Crown } from 'lucide-react';
import { adminApi } from '../../api';

const TIER_LABEL = { none: 'Base', featured: 'In evidenza', top3: 'Top 3', recommended: 'Consigliato da YUP' };
const TIER_COLOR = { none: 'text-gray-500', featured: 'text-blue-600', top3: 'text-amber-600', recommended: 'text-[color:var(--yup-primary)]' };

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function AdminSponsorUpgrades() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.sponsorUpgrades(statusFilter || undefined)); }
    catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const act = async (id, status) => {
    if (!window.confirm(`Confermi ${status === 'approved' ? 'approvazione' : status === 'paid' ? 'attivazione pagamento' : 'rifiuto'}?`)) return;
    setBusy(id);
    try {
      const notes = status === 'rejected' ? window.prompt('Motivo del rifiuto (opzionale):') : null;
      await adminApi.updateSponsorUpgrade(id, { status, admin_notes: notes || undefined });
      await load();
      setMsg('Richiesta aggiornata ✓');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa richiesta?')) return;
    setBusy(id);
    try { await adminApi.deleteSponsorUpgrade(id); await load(); }
    catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(null); }
  };

  const counts = items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Crown size={22} className="text-[color:var(--yup-primary)]"/> Richieste upgrade sponsor
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Approva o rifiuta le richieste di upgrade tier sponsorizzazione inviate dagli sponsor.
          </p>
        </div>
        <button onClick={load} data-testid="upgrades-refresh"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50 text-sm text-gray-700">
          <RefreshCcw size={14}/> Aggiorna
        </button>
      </div>

      {msg && <div className="mb-4 px-4 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm">{msg}</div>}

      {/* Filter chips */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="upgrades-status-filter">
        {[
          { k: '', l: 'Tutte', count: items.length },
          { k: 'pending', l: 'In attesa', count: counts.pending || 0 },
          { k: 'approved', l: 'Approvate', count: counts.approved || 0 },
          { k: 'paid', l: 'Pagate', count: counts.paid || 0 },
          { k: 'rejected', l: 'Rifiutate', count: counts.rejected || 0 },
          { k: 'cancelled', l: 'Annullate', count: counts.cancelled || 0 },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setStatusFilter(f.k)}
            data-testid={`upgrades-filter-${f.k || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              statusFilter === f.k
                ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >{f.l} {f.count > 0 && <span className="ml-1 opacity-70">({f.count})</span>}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="inline animate-spin text-gray-400" size={24}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-white rounded-lg border border-gray-200">
          <Crown size={40} className="mx-auto mb-3 text-gray-300"/>
          Nessuna richiesta {statusFilter && `nello stato "${statusFilter}"`}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Attività</th>
                  <th className="text-left px-4 py-3">Sponsor</th>
                  <th className="text-left px-4 py-3">Da → A</th>
                  <th className="text-right px-4 py-3">Δ Prezzo</th>
                  <th className="text-center px-4 py-3">Durata</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(u => (
                  <tr key={u.id} className="border-t border-gray-100" data-testid={`upgrade-row-${u.id}`}>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString('it-IT')}<br/>
                      <span className="text-gray-400">{new Date(u.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-medium">{u.activity_name || u.activity_id}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{u.sponsor_id}</td>
                    <td className="px-4 py-3">
                      <span className={TIER_COLOR[u.current_level]}>{TIER_LABEL[u.current_level]}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className={`font-semibold ${TIER_COLOR[u.target_level]}`}>{TIER_LABEL[u.target_level]}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-[color:var(--yup-primary)] font-bold">
                      €{Number(u.price_delta).toFixed(0)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{u.duration_months || 1}m</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full border ${STATUS_STYLE[u.status] || STATUS_STYLE.pending}`}>{u.status}</span>
                      {u.admin_notes && <div className="text-[10px] text-gray-400 mt-1 max-w-[200px] truncate" title={u.admin_notes}>Nota: {u.admin_notes}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {u.status === 'pending' && (
                        <>
                          <button
                            onClick={() => act(u.id, 'approved')}
                            disabled={busy === u.id}
                            data-testid={`upgrade-approve-${u.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50 mr-1"
                            title="Approva e attiva subito"
                          >
                            <Check size={12}/> Approva
                          </button>
                          <button
                            onClick={() => act(u.id, 'rejected')}
                            disabled={busy === u.id}
                            data-testid={`upgrade-reject-${u.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-500 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
                          >
                            <X size={12}/> Rifiuta
                          </button>
                        </>
                      )}
                      {u.status === 'approved' && (
                        <button
                          onClick={() => act(u.id, 'paid')}
                          disabled={busy === u.id}
                          data-testid={`upgrade-mark-paid-${u.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold disabled:opacity-50"
                        >
                          <CheckCircle2 size={12}/> Segna come pagata
                        </button>
                      )}
                      {['rejected', 'cancelled', 'paid'].includes(u.status) && (
                        <button
                          onClick={() => del(u.id)}
                          disabled={busy === u.id}
                          data-testid={`upgrade-delete-${u.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs"
                        >
                          <XCircle size={12}/> Elimina
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
